import React, { useState } from 'react';
import { FolderIcon, ClockIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export interface ArtworkVersion {
  versionNumber: number;
  imageData: string;
  operation: 'upload' | 'remove_background' | 'upscale' | 'trim';
  timestamp: Date;
  dimensions: {
    width: number;
    height: number;
  };
}
export interface Artwork {
  id: string;
  name: string;
  versions: ArtworkVersion[];
  currentVersion: number;
  createdAt: Date;
}
interface ArtworkLibraryProps {
  artworks: Artwork[];
  onSelectArtwork: (imageData: string) => void;
}
export function ArtworkLibrary({
  artworks,
  onSelectArtwork
}: ArtworkLibraryProps) {
  const [expandedArtworkId, setExpandedArtworkId] = useState<string | null>(null);
  if (artworks.length === 0) {
    return <div className="text-center py-12" data-id="element-53">
        <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-54" />
        <p className="text-sm text-gray-500" data-id="element-55">No artwork yet</p>
        <p className="text-xs text-gray-400 mt-1" data-id="element-56">
          Upload designs to build your library
        </p>
      </div>;
  }
  const toggleExpanded = (artworkId: string) => {
    setExpandedArtworkId(expandedArtworkId === artworkId ? null : artworkId);
  };
  const getOperationLabel = (operation: ArtworkVersion['operation']) => {
    switch (operation) {
      case 'upload':
        return 'Original Upload';
      case 'remove_background':
        return 'Background Removed';
      case 'upscale':
        return 'Upscaled (4K)';
      case 'trim':
        return 'Trimmed';
      default:
        return operation;
    }
  };
  return <div className="space-y-3" data-id="element-57">
      <div className="flex items-center gap-2 mb-4" data-id="element-58">
        <FolderIcon className="w-5 h-5 text-gray-700" data-id="element-59" />
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-60">
          My Artwork Library
        </h3>
        <span className="text-xs text-gray-500" data-id="element-61">({artworks.length})</span>
      </div>

      <div className="space-y-2" data-id="element-62">
        {artworks.map(artwork => {
        const isExpanded = expandedArtworkId === artwork.id;
        const latestVersion = artwork.versions[artwork.currentVersion - 1];
        return <div key={artwork.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-63">
              {/* Artwork Header */}
              <div className="p-3" data-id="element-64">
                <div className="flex items-start gap-3" data-id="element-65">
                  <img src={latestVersion.imageData} alt={artwork.name} className="w-16 h-16 object-contain rounded bg-gray-50 border border-gray-200" data-id="element-66" />
                  <div className="flex-1 min-w-0" data-id="element-67">
                    <h4 className="text-sm font-medium text-gray-900 truncate" data-id="element-68">
                      {artwork.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-69">
                      {artwork.versions.length} version
                      {artwork.versions.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5" data-id="element-70">
                      {latestVersion.dimensions.width} ×{' '}
                      {latestVersion.dimensions.height} px
                    </p>
                  </div>
                  <button onClick={() => toggleExpanded(artwork.id)} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-71">
                    {isExpanded ? <ChevronUpIcon className="w-4 h-4" data-id="element-72" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-73" />}
                  </button>
                </div>

                {/* Quick Add Latest Version */}
                <button onClick={() => onSelectArtwork(latestVersion.imageData)} className="w-full mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1.5" data-id="element-74">
                  <PlusIcon className="w-3.5 h-3.5" data-id="element-75" />
                  Add to Design
                </button>
              </div>

              {/* Version History (Expanded) */}
              {isExpanded && <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2" data-id="element-76">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-77">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-78" />
                    <span className="text-xs font-medium text-gray-700" data-id="element-79">
                      Version History
                    </span>
                  </div>

                  {artwork.versions.map(version => <div key={version.versionNumber} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200" data-id="element-80">
                      <img src={version.imageData} alt={`Version ${version.versionNumber}`} className="w-10 h-10 object-contain rounded bg-gray-50" data-id="element-81" />
                      <div className="flex-1 min-w-0" data-id="element-82">
                        <p className="text-xs font-medium text-gray-900" data-id="element-83">
                          v{version.versionNumber} -{' '}
                          {getOperationLabel(version.operation)}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-84">
                          {version.dimensions.width} ×{' '}
                          {version.dimensions.height} px
                        </p>
                      </div>
                      <button onClick={() => onSelectArtwork(version.imageData)} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors" data-id="element-85">
                        Use
                      </button>
                    </div>)}
                </div>}
            </div>;
      })}
      </div>
    </div>;
}